import {
  GetPrototypeItem,
  SavePrototypeFormValues,
} from '../../../service/prototype/type';
import { TreeData } from '../List/SummaryByCategoryNodes';

export const safeParseJson = (jsonString: string) => {
  try {
    return jsonString ? JSON.parse(jsonString) : {};
  } catch (e) {
    return {};
  }
};

export const prototypeToFormValues = (
  prototype: GetPrototypeItem,
  categoryTreeJson?: any | null
) => {
  const {
    source_url,
    market_unit_usd_price,
    prototype_name,
    category,
    main_image_cos_urls,
    scene_image_cos_urls,
    detail_image_cos_urls,
    size_image_cos_urls,
    other_image_cos_urls,
    video_cos_urls,
    features,
    style,
    wash_instructions,
    product_scale_json,
    leaf_category_keys,
  } = prototype;
  const productScaleObj = safeParseJson(product_scale_json);
  const { colors } = productScaleObj;

  return {
    source_url,
    category_code: category,
    prototype_name,
    main_image_cos_urls,
    scene_image_cos_urls,
    detail_image_cos_urls,
    size_image_cos_urls,
    other_image_cos_urls,
    video_cos_urls,
    features,
    style,
    wash_instructions,
    market_unit_usd_price,
    colors,
    leaf_category_options: leaf_category_keys
      ? getCategoryLeafTreeOptions(leaf_category_keys, categoryTreeJson)
      : [],
  } as SavePrototypeFormValues;
};

export const translateSourceName = (sourceName: string) => {
  switch (true) {
    case sourceName.includes('top fabric'):
      return sourceName.replace('top fabric', '外壳面布');
    case sourceName.includes('bottom fabric'):
      return sourceName.replace('bottom fabric', '外壳底布');
    case sourceName.includes('lining fabric'):
      return sourceName.replace('lining fabric', '外壳墙布');
    case sourceName.includes('filling'):
      return sourceName.replace('filling', '填充');
    case sourceName.includes('inner core fabric'):
      return sourceName.replace('inner core fabric', '内胆外壳');
    case sourceName.includes('binding fabric'):
      return sourceName.replace('binding fabric', '包边布');
    case sourceName.includes('skirt fabric'):
      return sourceName.replace('skirt fabric', '床裙面料');
    default:
      return sourceName;
  }
};

const convertInchToCm = (dNum: number) => dNum * 2.54;

export const convertToCm = ({
  dNum,
  isCm,
}: {
  dNum: number;
  isCm: boolean;
}) => {
  return isCm ? dNum : convertInchToCm(dNum);
};

export const getCategoryLeafTreeOptions = (
  leaf_category_keys: string[],
  categoryTreeJson?: any | null
) => {
  const leafOptions: { value: string; label: string }[] = [];
  const traverseTree = (node: any) => {
    if (node.children && node.children.length > 0) {
      node.children.forEach((child: any) => traverseTree(child));
    } else {
      if (node.key && node.title && leaf_category_keys.includes(node.key)) {
        leafOptions.push({
          value: node.key,
          label: node.title,
        });
      }
    }
  };
  categoryTreeJson?.forEach((node: any) => traverseTree(node));
  return leafOptions;
};

/* function getPathsToRoot(tree, leafKeys) {
  // key -> parent key
  const parentMap = new Map();

  // Build parent relationships
  function buildParentMap(nodes, parentKey = null) {
    for (const node of nodes) {
      if (parentKey !== null) {
        parentMap.set(node.key, parentKey);
      }

      if (node.children?.length) {
        buildParentMap(node.children, node.key);
      }
    }
  }

  buildParentMap(tree);

  // Walk from each leaf to the root
  return leafKeys.map((leafKey) => {
    const path = [];
    let currentKey = leafKey;

    while (currentKey !== undefined && currentKey !== null) {
      path.push(currentKey);
      currentKey = parentMap.get(currentKey);
    }

    // Currently: leaf -> ... -> root
    return path;
  });
} */

export const buildParentMapAndKeyAndTitleMapInner = (
  nodes: TreeData,
  parentKey: string | null = null,
  parentMap: Record<string, string> = {},
  keyAndTitleMap: Record<string, string> = {}
) => {
  for (const node of nodes) {
    if (node.key && node.title) {
      keyAndTitleMap[node.key] = node.title;
    }
    if (parentKey !== null) {
      parentMap[node.key] = parentKey;
    }
    if (node.children?.length) {
      buildParentMapAndKeyAndTitleMapInner(
        node.children,
        node.key,
        parentMap,
        keyAndTitleMap
      );
    }
  }
  return { parentMap, keyAndTitleMap };
};

export const buildParentMapAndKeyAndTitleMap = (
  nodes: TreeData,
  parentKey: string | null = null
) => {
  const parentMap: Record<string, string> = {};
  const keyAndTitleMap: Record<string, string> = {};
  buildParentMapAndKeyAndTitleMapInner(
    nodes,
    parentKey,
    parentMap,
    keyAndTitleMap
  );
  return { parentMap, keyAndTitleMap };
};

export const getSuggestedName = ({
  leafCategoryOptions,
  parentMap,
  keyAndTitleMap,
}: {
  leafCategoryOptions: { value: string; label: string }[];
  categoryTreeJson?: any | null;
  parentMap?: Record<string, string> | null;
  keyAndTitleMap?: Record<string, string> | null;
}) => {
  if (
    !leafCategoryOptions ||
    leafCategoryOptions.length === 0 ||
    !parentMap ||
    !keyAndTitleMap
  ) {
    return '';
  }

  // first compute paths for every leaf category option to the root
  const titlesArrays = leafCategoryOptions
    .map((option) => option.value)
    .map((leafKey) => {
      const titles = [];
      let currentKey = leafKey;
      while (currentKey !== undefined && currentKey !== null) {
        titles.push(keyAndTitleMap[currentKey]);
        currentKey = parentMap[currentKey];
      }
      return titles.filter(Boolean);
    });
  // titlesArrays are of the form
  // [[x1,x2,x3,x4,x5,x6]
  //  [y1,y2,y3,x4,x5,x6]]
  const minLength = Math.min(...titlesArrays.map((p) => p.length));
  let commonLength = 0;
  for (let i = 1; i <= minLength; i++) {
    const value = titlesArrays[0][titlesArrays[0].length - i];
    if (titlesArrays.every((titles) => titles[titles.length - i] === value)) {
      commonLength = i;
    } else {
      break;
    }
  }
  const commonSubString = titlesArrays[0].slice(
    titlesArrays[0].length - commonLength
  );
  const differentSubStrings = titlesArrays.map((titles) =>
    titles.slice(0, titles.length - commonLength)
  );
  const finalName =
    differentSubStrings.map((sub) => sub.join(' ')).join(' ') +
    ' ' +
    commonSubString.join(' ');
  return finalName;
};
